import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneCall, Lock, ChevronDown, Copy, Check, ExternalLink, PlayCircle, Code2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { StickyBack } from "../../components/StickyBack";
import { BOOKCALL_PAGE, chapterText } from "./hubBookCallContent";
import { MASTERCLASS_VIDEO, MASTERCLASS_INTRO, MASTERCLASS_PARTS, MASTERCLASS_JOURNEY } from "./hubMasterclassContent";
import { CompletionToggle } from "./ProgrammePage";
import { CommentField, FieldCommentsProvider } from "../../components/CommentField";

const DARK = { backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.55), transparent 60%)" };

function CopyBtn({ text, testid, dark, label = "Copier" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setDone(true);
    toast.success("Copié ✨");
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <button data-testid={testid} onClick={copy}
      className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition ${dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-bordeaux text-white hover:bg-bordeaux-dark"}`}>
      {done ? <Check size={13} /> : <Copy size={13} />}{done ? "Copié" : label}
    </button>
  );
}

function Question({ b }) {
  return (
    <div className="rounded-2xl border border-line bg-cream/60 p-4">
      <p className="text-sm font-medium text-ink"><span className="mr-2 font-semibold text-bordeaux">{b.id}.</span>{b.label}</p>
      {b.hint && <p className="mt-1 text-[13px] font-light italic leading-relaxed text-ink-muted">{b.hint}</p>}
      <div className="mt-2 space-y-1">
        {Array.from({ length: b.answers }).map((_, i) => (
          <p key={i} className="text-sm text-bordeaux/60">→ {b.answers > 1 ? `${i + 1}.` : "..."}</p>
        ))}
      </div>
    </div>
  );
}

function CodeBlock({ b }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="bookcall-code" className="overflow-hidden rounded-2xl text-white" style={DARK}>
      <div className="flex flex-wrap items-center gap-3 border-b border-white/10 p-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10"><Code2 size={17} /></span>
        <div className="flex-1"><p className="text-xs font-semibold uppercase tracking-[0.18em]">{b.label}</p><p className="text-xs font-light text-white/60">{b.hint}</p></div>
        <CopyBtn text={b.text} testid="bookcall-copy-code" dark />
      </div>
      <pre className={`overflow-x-auto px-4 py-3 font-mono text-[11px] leading-relaxed text-white/75 ${open ? "max-h-[560px] overflow-y-auto" : "max-h-40 overflow-hidden"}`}>{b.text}</pre>
      <button data-testid="bookcall-code-toggle" onClick={() => setOpen(!open)} className="w-full border-t border-white/10 py-2.5 text-xs font-medium text-[#F3D2D9] hover:bg-white/5">
        {open ? "Réduire le code" : "Afficher tout le code"}
      </button>
    </div>
  );
}

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 break-all rounded-full border border-bordeaux-border bg-white px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint">{children}<ExternalLink size={12} className="shrink-0" /></a>
);

function Block({ b, testid }) {
  switch (b.t) {
    case "question": return <Question b={b} />;
    case "h": return <p className="pt-2 text-sm font-semibold text-bordeaux">{b.text}</p>;
    case "tip": return <p className={`whitespace-pre-line rounded-xl bg-bordeaux-tint/50 px-4 py-3 text-sm font-light leading-relaxed text-bordeaux-dark ${b.italic ? "italic" : ""}`}>{b.text}</p>;
    case "example": return <p className="whitespace-pre-line border-l-2 border-bordeaux-border pl-3 text-sm font-light italic text-ink-muted">{b.text}</p>;
    case "done": return <p className="flex items-center gap-2 rounded-xl bg-bordeaux px-4 py-3 text-sm font-medium text-white"><CheckCircle2 size={16} />{b.text}</p>;
    case "copy": return (
      <div className="rounded-2xl border border-bordeaux-border bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-bordeaux">{b.label}</p>
          <CopyBtn text={b.text} testid={`${testid}-copy`} />
        </div>
        <p className="whitespace-pre-line px-4 py-3 text-[13px] font-light leading-relaxed text-ink-soft">{b.text}</p>
      </div>
    );
    case "code": return <CodeBlock b={b} />;
    case "bullets": return <div className="space-y-1.5">{b.items.map((x, i) => <p key={i} className="flex gap-2.5 text-sm font-light text-ink-soft"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-bordeaux" />{x}</p>)}</div>;
    case "steps": return (
      <ol className="space-y-2">
        {b.items.map((x, i) => (
          <li key={i} className="flex gap-3 text-sm font-light leading-relaxed text-ink-soft">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bordeaux-tint text-[11px] font-semibold text-bordeaux">{i + 1}</span>
            <span className="min-w-0 break-words pt-0.5">{typeof x === "string" ? x : x.text}
              {typeof x !== "string" && Array.isArray(x.sub) && <span className="mt-1.5 block space-y-1">{x.sub.map((s, k) => <span key={k} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-bordeaux" />{s}</span>)}</span>}
            </span>
          </li>
        ))}
      </ol>
    );
    case "link": return <div className="space-y-2"><p className="text-sm font-light text-ink-soft">{b.before}</p><Ext href={b.href}>{b.label}</Ext><p className="break-all text-[11px] text-ink-muted">{b.href}</p></div>;
    case "video": return <div className="space-y-2">{b.before && <p className="text-sm font-light text-ink-soft">{b.before}</p>}<Ext href={b.href}><PlayCircle size={13} className="shrink-0" />Regarder le tuto vidéo</Ext><p className="break-all text-[11px] text-ink-muted">{b.href}</p></div>;
    default: return <p className={`text-sm leading-relaxed ${b.strong ? "font-medium text-ink" : "font-light text-ink-soft"}`}>{b.text}</p>;
  }
}

function Step({ s, id }) {
  return (
    <div data-testid={id} className="rounded-[24px] border border-line bg-white p-5 shadow-card sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <span className="text-xl">{s.num}</span>
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-bordeaux">{s.title}</p>
      </div>
      <div className="space-y-3">{s.blocks.map((b, i) => <Block key={i} b={b} testid={`${id}-b${i}`} />)}</div>
    </div>
  );
}

function Chapter({ c, i, open, onToggle }) {
  return (
    <section data-testid={`bookcall-chapter-${i}`} className="space-y-4">
      <button data-testid={`bookcall-chapter-toggle-${i}`} onClick={onToggle} style={DARK}
        className="flex w-full items-center gap-4 rounded-[28px] p-5 text-left text-white shadow-[0_16px_48px_rgba(45,24,30,0.22)] transition hover:brightness-110 sm:p-7">
        <span className="text-2xl sm:text-3xl">{c.emoji}</span>
        <span className="flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D2D9]">{c.kicker} 👉🏻</span>
          <span className="mt-1 block font-sans text-xl font-medium sm:text-2xl">{c.title}</span>
        </span>
        <ChevronDown size={22} className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="space-y-4 pb-2">
              <div className="flex justify-end"><CopyBtn text={chapterText(c)} testid={`bookcall-copy-chapter-${i}`} label="Copier le contenu de toute cette partie" /></div>
              {c.intro && <p className="rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-4 text-sm font-light leading-relaxed text-ink-soft">{c.intro}</p>}
              {c.steps.map((s, k) => <Step key={k} s={s} id={`bookcall-step-${i}-${k}`} />)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Part({ p, i, open, onToggle }) {
  return (
    <section data-testid={`mc-part-${i}`} className="space-y-4">
      <button data-testid={`mc-part-toggle-${i}`} onClick={onToggle} style={DARK} className="flex w-full items-center gap-4 rounded-[28px] p-5 text-left text-white shadow-[0_16px_48px_rgba(45,24,30,0.22)] transition hover:brightness-110 sm:p-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 font-sans text-lg font-medium">{p.num}</span>
        <span className="flex-1">
          <span className="block font-sans text-lg font-medium sm:text-xl">{p.title}</span>
          <span className="mt-0.5 block text-xs font-light text-[#F3D2D9]">{p.kicker}</span>
        </span>
        <ChevronDown size={22} className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="space-y-3 rounded-[24px] border border-line bg-white p-5 shadow-card sm:p-6">{p.blocks.map((b, k) => <Block key={k} b={b} testid={`mc-${i}-${k}`} />)}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function MasterclassWriter() {
  const [v, setV] = useState(null);
  const [saved, setSaved] = useState(true);
  useEffect(() => { api.get("/client/submissions/hub-masterclass").then(({ data }) => setV(data?.texte || "")).catch(() => setV("")); }, []);
  useEffect(() => {
    if (v === null || saved) return undefined;
    const t = setTimeout(() => { api.put("/client/submissions/hub-masterclass", { key: "texte", value: v }).then(() => setSaved(true)).catch(() => toast.error("Impossible d'enregistrer, réessaie.")); }, 900);
    return () => clearTimeout(t);
  }, [v, saved]);
  return (
    <FieldCommentsProvider page="hub-masterclass">
    <div data-testid="mc-writer" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div><h2 className="font-sans text-xl font-medium text-ink sm:text-2xl">Rédige ta masterclass</h2><p className="text-sm font-light text-ink-muted">Suis les 7 parties dans l'ordre, ton texte est enregistré automatiquement.</p></div>
        <span data-testid="mc-writer-status" className="text-xs text-ink-muted">{v === null ? "Chargement…" : saved ? "Enregistré ✓" : "Enregistrement…"}</span>
      </div>
      {v === null ? <div className="min-h-[520px] rounded-[24px] border border-line bg-white shadow-card" />
        : <CommentField fieldKey="texte" testid="mc-writer-input" value={v} onChange={(html) => { setV(html); setSaved(false); }}
          placeholder={"1. Introduction · 2. Identification · 3. Ta présentation · 4. Fausses croyances · 5. Ta méthode · 6. Conclusion · 7. CTA"}
          className="min-h-[520px] rounded-[24px] border border-line bg-white p-5 text-sm font-light leading-relaxed text-ink shadow-card focus:border-bordeaux sm:p-7" />}
    </div>
    </FieldCommentsProvider>
  );
}

function MasterclassContent() {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-8" data-testid="mc-content">
      <div className="space-y-3 rounded-[24px] border border-line bg-white p-5 shadow-card sm:p-6">{MASTERCLASS_INTRO.map((b, k) => <Block key={k} b={b} testid={`mc-intro-${k}`} />)}</div>
      <p className="text-sm font-light text-ink-soft">Pour voir ce que donne cette structure une fois filmée, regarde ma masterclass en exemple :{" "}
        <a data-testid="mc-video" href={MASTERCLASS_VIDEO} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-bordeaux underline underline-offset-2 hover:text-bordeaux-dark"><PlayCircle size={14} />voir la masterclass<ExternalLink size={12} /></a>
      </p>
      <div className="space-y-4">{MASTERCLASS_PARTS.map((p, i) => <Part key={i} p={p} i={i} open={open === i} onToggle={() => setOpen(open === i ? null : i)} />)}</div>
      <div data-testid="mc-journey" className="rounded-[24px] border border-bordeaux-border bg-bordeaux-tint/30 p-5 sm:p-6">
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-bordeaux">Le parcours psychologique de ta masterclass</p>
        <ol className="mt-4 space-y-2.5">{MASTERCLASS_JOURNEY.map(([t, q], i) => (
          <li key={i} className="flex gap-3 text-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bordeaux text-[11px] font-semibold text-white">{i + 1}</span><span><span className="font-medium text-ink">{t}</span><span className="block font-light italic text-ink-soft">{q}</span></span></li>
        ))}</ol>
      </div>
      <MasterclassWriter />
      <CompletionToggle spaced ckey="hub-masterclass" title="Rédige ta masterclass" message="a terminé de rédiger sa masterclass" />
    </div>
  );
}

const BC_TABS = [["masterclass", "1. Rédige ta masterclass"], ["design", "2. Crée ta page"]];

export function MasterclassAdminView({ clientId }) {
  const [v, setV] = useState(null);
  const [saved, setSaved] = useState(true);
  useEffect(() => { api.get(`/admin/clients/${clientId}/submissions/hub-masterclass`).then(({ data }) => setV(data?.texte || "")).catch(() => setV("")); }, [clientId]);
  useEffect(() => {
    if (v === null || saved) return undefined;
    const t = setTimeout(() => { api.put(`/admin/clients/${clientId}/submissions/hub-masterclass`, { key: "texte", value: v }).then(() => setSaved(true)).catch(() => toast.error("Impossible d'enregistrer, réessaie.")); }, 900);
    return () => clearTimeout(t);
  }, [v, saved, clientId]);
  return (
    <FieldCommentsProvider page="hub-masterclass" clientId={clientId}>
    <div data-testid="mc-admin-view" className="mb-8 space-y-2">
      <div className="flex items-end justify-between gap-2">
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-bordeaux">Sa masterclass rédigée</p>
        {v !== null && <span data-testid="mc-admin-status" className="text-xs text-ink-muted">{saved ? "Enregistré ✓" : "Enregistrement…"}</span>}
      </div>
      {v === null ? <div className="rounded-[24px] border border-line bg-white p-5 text-sm text-ink-muted">Chargement…</div>
        : <CommentField fieldKey="texte" testid="mc-admin-text" value={v} onChange={(html) => { setV(html); setSaved(false); }} placeholder="Pas encore rédigée."
          className="min-h-[200px] rounded-[24px] border border-line bg-white p-5 text-sm font-light leading-relaxed text-ink shadow-card focus:border-bordeaux" />}
    </div>
    </FieldCommentsProvider>
  );
}

export function BookCallContent() {
  const [open, setOpen] = useState(null);
  const P = BOOKCALL_PAGE;
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <p className="text-sm font-medium text-ink">{P.exampleIntro}</p>
        <img data-testid="bookcall-example-img" src={P.exampleImage} alt="Exemple de page book a call" className="mx-auto w-full max-w-md rounded-[24px] border border-line shadow-card" />
      </div>
      <div className="space-y-5">
        {P.chapters.map((c, i) => <Chapter key={i} c={c} i={i} open={open === i} onToggle={() => setOpen(open === i ? null : i)} />)}
      </div>
    </div>
  );
}

export default function HubBookCall() {
  const navigate = useNavigate();
  const [locked, setLocked] = useState(null);
  const [tab, setTab] = useState("masterclass");
  useEffect(() => {
    api.get("/client/hub-pages").then(({ data }) => setLocked(!!data.booking_locked)).catch(() => setLocked(false));
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-8">
      <StickyBack onClick={() => navigate("/espace/hub")}>Revenir à The Elevate Hub</StickyBack>
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-bordeaux text-white shadow-[0_8px_24px_rgba(123,17,58,0.35)]"><PhoneCall size={24} /></span>
        <div>
          <h1 className="font-sans text-2xl font-medium text-ink sm:text-4xl">{BOOKCALL_PAGE.title}</h1>
          <p className="mt-1 text-sm font-light text-ink-muted">{BOOKCALL_PAGE.subtitle}</p>
        </div>
      </div>
      {locked === true && (
        <div data-testid="bookcall-locked" className="flex items-center gap-3 rounded-2xl border border-line bg-white p-6 text-ink-muted"><Lock size={18} /> Cette ressource n'est pas encore disponible.</div>
      )}
      {locked === false && (
        <div className="flex w-full gap-1 overflow-x-auto rounded-full bg-[#2D181E] p-1.5">
          {BC_TABS.map(([k, l]) => (
            <button key={k} data-testid={`bookcall-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 whitespace-nowrap rounded-full px-3 py-2.5 text-center text-xs transition-colors duration-200 sm:px-5 sm:text-sm ${tab === k ? "bg-bordeaux font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.6)]" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      )}
      {locked === false && tab === "masterclass" && <MasterclassContent />}
      {locked === false && tab === "design" && <BookCallContent />}
      {locked === false && tab === "design" && <CompletionToggle ckey="hub-book-a-call" title="Tutoriel : page book a call" message="a terminé sa page book a call" withLink />}
    </motion.div>
  );
}
